import { DefaultGenerics, EnrichedReactionAPIResponse, NewActivity, StreamClient, StreamFeed, UR } from 'getstream';
import { QueryClient } from '@tanstack/react-query';
import { staleTimeForSession, staleTimeInFiveMinutes, staleTimeInOneMinute } from '../reactQueryCore';
import useCustomQuery from '../useCustomQuery';
import useCustomInfiniteQuery from '../useCustomInfiniteQuery';
import { get, post, put, httpDelete } from './ApiWrapper';
import useCustomMutation from '../useCustomMutation';
import { feedActivityPageSize } from '../constants';
import { NetworkSocialProfile, UserSocialProfile } from '../../models/SocialProfile';
import { ActivityData, ActivityFileInfo, LinkPreviewResponse } from '../../models/SocialFeed';
import { addQueryParam } from '../../lib/utils';
import { imageFileType } from '../fileUtils';

export const feedsApiBasePath = 'Feeds';

const streamFeedString = 'streamFeed';
const reactionKindLike = 'like';
const reactionKindComment = 'comment';
const reactionKindFlag = 'flag';

const feedUserQueryKey = (feedUserId: string) => [streamFeedString, 'users', feedUserId];
const feedCommunityUsersQueryKey = [streamFeedString, 'users', 'community'];
const feedActivitiesQueryKey = (feedName: string) => [streamFeedString, 'activities', feedName];
const activityCommentsQueryKey = (activityId: string) => [streamFeedString, 'activities', activityId, 'comments'];
const activityLikeAndCommentCountQueryKey = (activityId: string) => [
	streamFeedString,
	'activities',
	activityId,
	'likeAndCommentCount'
];

export const useFeedToken = () => {
	return useCustomQuery({
		queryKey: [feedsApiBasePath, 'token'],
		queryFn: async (): Promise<string> => post(`${feedsApiBasePath}/Token`, null),
		staleTime: staleTimeForSession,
		showRetryMessageIfError: true
	});
};

export const useCommunityUsers = () => {
	return useCustomQuery({
		queryKey: feedCommunityUsersQueryKey,
		queryFn: async (): Promise<UserSocialProfile[]> => get(`${feedsApiBasePath}/Users/Community`),
		showRetryMessageIfError: true,
		staleTime: staleTimeInFiveMinutes // Use shorter 5 minutes cache time for community users for now
	});
};

export const useEventNetworkUsers = () => {
	return useCustomQuery({
		queryKey: [feedsApiBasePath, 'users', 'network'],
		queryFn: async (): Promise<NetworkSocialProfile[]> => get(`${feedsApiBasePath}/Users/Network`),
		showRetryMessageIfError: true,
		staleTime: staleTimeInFiveMinutes // Use shorter 5 minutes cache time for network users for now
	});
};

export const useFeedLinkPreview = (url: string) => {
	return useCustomQuery({
		queryKey: [feedsApiBasePath, 'linkPreview', url],
		queryFn: async (): Promise<LinkPreviewResponse | null> => {
			const queryParams = [{ key: 'url', value: url }];
			const requestUrl = addQueryParam(`${feedsApiBasePath}/LinkPreview`, queryParams);
			return get(requestUrl);
		},
		showRetryMessageIfError: true,
		staleTime: staleTimeForSession,
		enabled: !!url // Only run if url is truthy
	});
};

// For Event Network page to view other user's profile
export const useFeedUserProfile = (feedClient: StreamClient, feedUserId: string, staleTime?: number) => {
	return useCustomQuery({
		queryKey: feedUserQueryKey(feedUserId),
		queryFn: async () => {
			const feedUser = await feedClient.user(feedUserId).get();
			return feedUser.data as unknown as UserSocialProfile;
		},
		staleTime: staleTime ?? staleTimeInOneMinute,
		enabled: !!feedClient && !!feedUserId,
		showRetryMessageIfError: true
	});
};

export const useCurrentFeedUserProfile = (feedClient: StreamClient) =>
	useFeedUserProfile(feedClient, feedClient?.currentUser?.id, staleTimeForSession);

export const useUpdateCurrentFeedUserProfile = (
	queryClient: QueryClient,
	feedClient: StreamClient,
	onSuccess: () => void
) => {
	return useCustomMutation({
		mutationFn: async (feedUserProfile: UserSocialProfile) => {
			await feedClient.user(feedClient.currentUser?.id).update(feedUserProfile as unknown as Partial<UR>);
		},
		onSuccess: async () => {
			onSuccess();
			await queryClient.invalidateQueries({ queryKey: feedUserQueryKey(feedClient?.currentUser?.id) });
			await queryClient.invalidateQueries({ queryKey: feedCommunityUsersQueryKey });
		},
		showRetryMessageIfError: true
	});
};

const fetchActivitiesPage = async (eventFeed: StreamFeed, offset: number) => {
	return eventFeed.get({
		limit: feedActivityPageSize,
		offset,
		withReactionCounts: true,
		withOwnReactions: true,
		withRecentReactions: true
	});
};

// Feed activity - GET
export const useInfiniteFeedActivities = (eventFeed: StreamFeed, feedName?: string) => {
	return useCustomInfiniteQuery({
		queryKey: feedActivitiesQueryKey(feedName),
		queryFn: async ({ pageParam = 0 }) => {
			const response = await fetchActivitiesPage(eventFeed, pageParam);
			return response;
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage: unknown, pages: unknown[]) => {
			const typedLastPage = lastPage as { next?: string };
			if (!typedLastPage.next) {
				return undefined; // no more pages
			}
			return pages.length * feedActivityPageSize; // next offset
		},
		staleTime: staleTimeInOneMinute,
		enabled: !!eventFeed
	});
};

// Feed activity - Add New
export const usePostFeedActivity = (
	queryClient: QueryClient,
	eventFeed: StreamFeed,
	feedName: string,
	onSuccess: () => void
) => {
	return useCustomMutation({
		mutationFn: async (activity: ActivityData) => {
			await eventFeed.addActivity(activity as unknown as NewActivity);
		},
		onSuccess: async () => {
			onSuccess();
			await queryClient.invalidateQueries({ queryKey: feedActivitiesQueryKey(feedName) });
		},
		showRetryMessageIfError: true
	});
};

// Feed activity - Update/Edit
export const useUpdateFeedActivity = (queryClient: QueryClient, feedName: string, onSuccess: () => void) => {
	return useCustomMutation({
		mutationFn: async (activity: ActivityData) => {
			await put(`${feedsApiBasePath}/${feedName}/Activity/${activity.id as string}`, {
				text: activity.text,
				files: activity.files,
				isEdited: true
			});
		},
		onSuccess: async () => {
			onSuccess();
			await queryClient.invalidateQueries({ queryKey: feedActivitiesQueryKey(feedName) });
		},
		showRetryMessageIfError: true
	});
};

// Feed activity - Delete
export const useDeleteFeedActivity = (queryClient: QueryClient, feedName: string, onSuccess: () => void) => {
	return useCustomMutation({
		mutationFn: async ({ activityId }: { activityId: string }): Promise<void> =>
			httpDelete(`${feedsApiBasePath}/${feedName}/Activity/${activityId}`),
		onSuccess: async () => {
			onSuccess();
			await queryClient.invalidateQueries({ queryKey: feedActivitiesQueryKey(feedName) });
		},
		showRetryMessageIfError: true
	});
};

// Activity reactions - Like count, Comment count - GET
// This activity data is only for reactions - Like, Comments count, so that when there is new like or comment change,
// will only trigger refetching this data so that won't cause unnecessary Activity content reloading for better user experience
export const useFeedActivityForReactionsCount = (eventFeed: StreamFeed, activityId: string) => {
	return useCustomQuery({
		queryKey: activityLikeAndCommentCountQueryKey(activityId),
		queryFn: async (): Promise<ActivityData> => {
			const response = await eventFeed.getActivityDetail(activityId, {
				withReactionCounts: true,
				withOwnReactions: true,
				withRecentReactions: true
			});
			const activity = response.results[0] as ActivityData;
			return activity;
		},
		staleTime: staleTimeInFiveMinutes,
		showRetryMessageIfError: true,
		enabled: !!eventFeed
	});
};

// Activity Like - Add or Remove
export const useAddOrRemoveLikeToActivity = (queryClient: QueryClient, feedClient: StreamClient) => {
	return useCustomMutation({
		mutationFn: async (activity: ActivityData) => {
			// This activity should be from above useFeedActivityForReactionsCount
			const ownLikes = activity.own_reactions.like;
			if (ownLikes && ownLikes.length > 0) {
				try {
					// Already liked, so remove the like
					await feedClient.reactions.delete(ownLikes[0].id);
					return;
				} catch {
					// Error intentionally ignored as the like might have been already deleted
				}
			}
			await feedClient.reactions.add(reactionKindLike, activity.id, {}, { userId: feedClient.currentUser?.id });
		},
		onSuccess: async (data: unknown, variables: ActivityData) => {
			await queryClient.invalidateQueries({ queryKey: activityLikeAndCommentCountQueryKey(variables.id) });
		},
		showRetryMessageIfError: true
	});
};

// Activity Comment - Add
export const useAddCommentToActivity = (queryClient: QueryClient, feedClient: StreamClient, onSuccess: () => void) => {
	return useCustomMutation({
		mutationFn: async ({ activityId, comment }: { activityId: string; comment: string }) => {
			await feedClient.reactions.add(
				reactionKindComment,
				activityId,
				{ text: comment },
				{ userId: feedClient.currentUser?.id }
			);
		},
		onSuccess: async (data: unknown, variables: { activityId: string; comment: string }) => {
			onSuccess();
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: activityCommentsQueryKey(variables.activityId) }),
				queryClient.invalidateQueries({ queryKey: activityLikeAndCommentCountQueryKey(variables.activityId) })
			]);
		},
		showRetryMessageIfError: true
	});
};

// Activity Comment - Delete
export const useDeleteCommentForActivity = (queryClient: QueryClient, feedClient: StreamClient) => {
	return useCustomMutation({
		mutationFn: async ({ commentId }: { activityId: string; commentId: string }) => {
			try {
				await feedClient.reactions.delete(commentId);
			} catch {
				// Error intentionally ignored as the comment might have been already deleted
			}
		},
		onSuccess: async (data: unknown, variables: { activityId: string; commentId: string }) => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: activityCommentsQueryKey(variables.activityId) }),
				queryClient.invalidateQueries({ queryKey: activityLikeAndCommentCountQueryKey(variables.activityId) })
			]);
		},
		showRetryMessageIfError: true
	});
};

// Activity Comments - GET
export const useActivityComments = (feedClient: StreamClient, activityId: string) => {
	return useCustomQuery({
		queryKey: activityCommentsQueryKey(activityId),
		queryFn: async () => {
			let response = await fetchComments(feedClient, activityId);
			let allComments = response.results;
			while (response.next) {
				// eslint-disable-next-line no-await-in-loop
				response = await fetchComments(feedClient, activityId, response.results[response.results.length - 1].id);
				allComments = allComments.concat(response.results as EnrichedReactionAPIResponse<DefaultGenerics>[]);
			}
			return allComments;
		},
		staleTime: staleTimeInFiveMinutes,
		showRetryMessageIfError: true,
		enabled: !!feedClient && !!activityId
	});
};

// Activity Comment - Like - Add or Remove
export const useAddOrRemoveLikeToComment = (queryClient: QueryClient, feedClient: StreamClient) => {
	return useCustomMutation({
		mutationFn: async (comment: EnrichedReactionAPIResponse) => {
			const commentLikes = comment.latest_children?.like ?? [];
			const likeFromCurrentUser = commentLikes.find((like) => like.user_id === feedClient.currentUser?.id);
			if (likeFromCurrentUser) {
				try {
					await feedClient.reactions.delete(likeFromCurrentUser.id);
				} catch {
					// Error intentionally ignored as the like might have been already deleted
				}
			} else {
				await feedClient.reactions.addChild(reactionKindLike, comment.id);
			}
		},
		onSuccess: async (data: unknown, variables: EnrichedReactionAPIResponse) => {
			await queryClient.invalidateQueries({ queryKey: activityCommentsQueryKey(variables.activity_id) });
		},
		showRetryMessageIfError: true
	});
};

const fetchComments = async (feedClient: StreamClient, activityId: string, lastCommentId?: string) => {
	return feedClient.reactions.filter({
		activity_id: activityId,
		kind: reactionKindComment,
		limit: 25,
		id_lt: lastCommentId
	});
};

export const useFlagFeedActivity = (feedClient: StreamClient) => {
	return useCustomMutation({
		mutationFn: async ({ activityId }: { activityId: string }) => {
			await feedClient.reactions.add(reactionKindFlag, activityId, {}, { userId: feedClient.currentUser?.id });
		},
		showRetryMessageIfError: true
	});
};

export const useFlagFeedComment = (feedClient: StreamClient) => {
	return useCustomMutation({
		mutationFn: async ({ commentId }: { commentId: string }) => {
			await feedClient.reactions.addChild(reactionKindFlag, commentId);
		},
		showRetryMessageIfError: true
	});
};

export const deleteFilesFromFeed = async (feedClient: StreamClient, files: ActivityFileInfo[]) => {
	// Delete images/files from Stream feed
	files.forEach((file) => {
		if (file.fileType === imageFileType) {
			feedClient.images.delete(file.fileUrl).catch(() => {});
		} else {
			feedClient.files.delete(file.fileUrl).catch(() => {});
		}
	});
};
